import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import type { RestaurantDetail } from '@/bindings/RestaurantDetail'
import type { TablePreview } from '@/bindings/TablePreview'
import { Button } from '@/components/button'
import { ConfirmButton } from '@/components/confirm'
import { Field, Input } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card } from '@/components/layout'
import { PreviewTable } from '@/features/cleanup/preview-table'
import { deleteRestaurant, previewDelete, restaurantKeys } from '@/features/restaurants/api'
import { useAuth } from '@/lib/auth-store'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { formatDate, todayIn } from '@/lib/time'

/**
 * K4: the hard delete, platform admins only. Disabled while the restaurant is active
 * or on hold; otherwise a check shows the row counts, and the typed URL name arms the
 * confirming press. Everything goes, accounts stay; there is no undo.
 */
export function DangerCard({ restaurant }: { restaurant: RestaurantDetail }) {
  const t = useT()
  const language = useLanguage()
  const auth = useAuth('platform')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [checked, setChecked] = useState<TablePreview[]>()
  const [slug, setSlug] = useState('')

  const check = useMutation({
    mutationFn: () => previewDelete(restaurant.id),
    onSuccess: (preview) => setChecked(preview.tables),
  })
  const remove = useMutation({
    mutationFn: () => deleteRestaurant(restaurant.id, slug.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: restaurantKeys.all })
      await navigate({ to: '/restaurants', search: { page: 1 } })
    },
  })

  if (auth.status !== 'authenticated' || !auth.user.is_admin) return null

  const active = restaurant.deactivated_at === null
  // The hold covers its last day, on the backend's UTC calendar.
  const held = restaurant.hold_until !== null && restaurant.hold_until >= todayIn('UTC')
  const blocker = active
    ? t('restaurants.deleteNeedsDeactivation')
    : held
      ? t(restaurant.hold_note ? 'restaurants.deleteHeldNote' : 'restaurants.deleteHeld', {
          date: formatDate(restaurant.hold_until!, language),
          note: restaurant.hold_note ?? '',
        })
      : undefined
  const rows = checked?.reduce((sum, table) => sum + table.rows, 0) ?? 0
  const armed = checked !== undefined && slug.trim() === restaurant.slug

  return (
    <Card className="border-danger/40 flex flex-col gap-4">
      <h2 className="text-danger text-lg font-semibold">{t('restaurants.danger')}</h2>
      <p className="text-muted text-sm">{t('restaurants.deleteHint')}</p>
      {blocker ? (
        <p role="note" className="text-sm">
          {blocker}
        </p>
      ) : (
        <>
          {!checked && (
            <Button variant="secondary" size="sm" className="self-start" loading={check.isPending} onClick={() => check.mutate()}>
              {t('restaurants.checkDelete')}
            </Button>
          )}
          <FormError error={check.error} />
          {checked && (
            <>
              <PreviewTable tables={checked} />
              <Field label={t('restaurants.deleteSlugLabel')} hint={t('restaurants.deleteTypeSlug', { slug: restaurant.slug })}>
                <Input value={slug} autoComplete="off" onChange={(e) => setSlug(e.target.value)} className="sm:max-w-xs" />
              </Field>
              <div className="flex justify-end">
                <ConfirmButton
                  label={t('restaurants.delete')}
                  variant="danger"
                  size="md"
                  disabled={!armed}
                  confirmLabel={t('restaurants.confirmDelete')}
                  confirmAriaLabel={t('restaurants.confirmDeleteOf', { name: restaurant.name })}
                  align="end"
                  note={<p className="text-sm">{t('restaurants.confirmDeleteText', { rows: rows.toLocaleString() })}</p>}
                  confirm={() => remove.mutateAsync()}
                  error={remove.error}
                />
              </div>
            </>
          )}
        </>
      )}
    </Card>
  )
}
